"use client"

import { removeWrappingPTags } from "@/utils/removeWrappingPTags"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import "swiper/css"
import "swiper/css/pagination"
import { Autoplay, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { transitionTime, useIsDesktop } from "ui"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getUmbracoMediaUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import { isVideoUrl } from "utils/mediaTypeDetection"
import { transformProperties } from "utils/simplifyData"
import { useUtmUrl } from "utils/useUtmUrl"
import CustomBreadcrumbs from "../CustomBreadcrumbs/CustomBreadcrumbs"
import VideoContainer from "./VideoContainer"

export const StyledHeroBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSwiper",
})<{ isSwiper: boolean; component?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  width: 100%;
  z-index: 4;
  ${mediaQueries.mobile} {
    flex-direction: column;
    justify-content: flex-end;
    margin-top: unset;
    padding-inline-start: 0;
    padding-inline-end: 0;
    min-height: 780px;
  }
  ${mediaQueries.tablet} {
    flex-direction: row;
    justify-content: flex-end;
    margin-top: -70px;
    height: 870px;
  }
  ${mediaQueries.desktopM} {
    flex-direction: row;
    justify-content: center;
    margin-top: unset;
    padding-top: unset;
    width: 100%;
    height: 720px;
  }
  ${mediaQueries.desktop} {
    flex-direction: row;
    height: 820px;
  }
`
const StyledSwiper = styled(Swiper)`
  display: flex;
  position: relative;
  padding-top: 0;
  z-index: 0;
  height: 100%;
  width: 100%;
  padding-bottom: 0;
  .swiper-wrapper {
    height: 100%;
  }
  .swiper-pagination {
    position: absolute;
    height: 100px;
    ${mediaQueries.desktopM} {
      inset: calc(50% - 50px) auto 0 40px;
    }
    ${mediaQueries.mobile} {
      bottom: 80px;
    }
    .swiper-pagination-bullet {
      width: 20px;
      height: 20px;
      background: ${colors.white};
      border: solid 1px ${colors.white};

      ${mediaQueries.mobile} {
        width: 14px;
        height: 14px;
      }
    }
  }
`

const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop) => prop !== "bgcolor" && prop !== "whiteText",
})<{ bgcolor?: string; whiteText?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: ${({ bgcolor }: { bgcolor?: string }) =>
    bgcolor ? `#${bgcolor.trim()}` : "transparent"};
  ${mediaQueries.desktopM} {
    padding-inline-start: 80px;
    padding-inline-end: 80px;
  }
  ${mediaQueries.desktop} {
    padding-inline-start: 180px;
    padding-inline-end: 150px;
  }
`

const StyledLimitWidth = styled(Box)`
  width: 100%;
  max-width: ${sizes.maxWidthContainer};
  display: flex;
  align-items: center;
  ${mediaQueries.mobile} {
    flex-direction: column;
    justify-content: flex-end;
    padding-inline-start: 0px;
    padding-inline-end: 0px;
  }
  ${mediaQueries.tablet} {
    justify-content: center;
    flex-direction: column;
  }
  ${mediaQueries.desktopM} {
    justify-content: space-between;
    flex-direction: row;
    gap: ${sizes.baseDouble};
  }
`

const StyledTitlesTextContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "whiteText",
})<{ whiteText: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  color: ${({ whiteText }) => (whiteText ? colors.white : colors.black)};
  ${mediaQueries.mobile} {
    max-width: 100%;
    align-items: center;
    text-align: center;
    padding: 134px 40px 0;
  }
  ${mediaQueries.desktopM} {
    flex: 1;
  }
`
const StyledTitle = styled(Typography)`
  text-align: start;
  margin-bottom: 12px;
  color: inherit;
  font-weight: 700;
  ${mediaQueries.mobile} {
    text-align: center;
  }
`
const StyledText = styled(Typography)<{ component?: string }>`
  text-align: start;
  margin-bottom: 12px;
  color: inherit;
  font-weight: 400;
  font-size: 24px;
  ${mediaQueries.mobile} {
    text-align: center;
  }
  ${mediaQueries.desktopXl} {
    font-size: 24px;
  }
`

const StyledSubTitle = styled.p`
  text-align: start;
  margin-bottom: 12px;
  color: inherit;
  ${mediaQueries.mobile} {
    text-align: center;
  }
  ${mediaQueries.desktopM} {
    font-weight: 400;
    line-height: 23px;
    font-size: 20px;
  }
  ${mediaQueries.desktopXl} {
    font-weight: 400;
    line-height: 27px;
    font-size: 18px;
  }
`
const StyledButtonsWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${sizes.baseTriple};
  gap: ${sizes.baseDouble};
  ${mediaQueries.mobile} {
    justify-content: center;
  }
  ${mediaQueries.tablet} {
    justify-content: center;
  }
  ${mediaQueries.desktopM} {
    justify-content: flex-start;
  }
`
const StyledImageContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-self: center;
  height: 90%;
  ${mediaQueries.mobile} {
    height: 380px;
    margin-bottom: 35px;
  }
  ${mediaQueries.tablet} {
    height: 380px;
  }
  ${mediaQueries.desktopM} {
    height: 495px;
    align-self: flex-end;
    max-width: 50%;
  }
  ${mediaQueries.desktop} {
    height: 495px;
  }
  ${mediaQueries.desktopXl} {
    height: 624px;
  }
`

const StyledVideoContainer = styled(Box)`
  width: 100%;
  max-width: 750px;
  aspect-ratio: 16 / 9;
  video {
    border-radius: ${sizes.borderRadiusLarge};
    object-fit: cover;
  }
  ${mediaQueries.mobile} {
    max-width: 95%;
    margin-bottom: 125px;
  }
  ${mediaQueries.tablet} {
    max-width: 100%;
  }
  ${mediaQueries.desktopM} {
    width: 50%;
  }
`

const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
  object-fit: contain;
`
interface HeroBannerProps {
  data: any
  id?: string
  breadcrumbs?: any
}
const HeroBanner = ({ data, id, breadcrumbs }: HeroBannerProps) => {
  const { bannerItems, mediaItems } = data
  const currentItems = bannerItems || { blocks: [mediaItems || data] }
  const isSwiper = bannerItems ? true : false
  const isDesktop = useIsDesktop()

  // Use the custom hook for UTM URL handling
  const getUrlWithUtm = useUtmUrl()
  return (
    <StyledHeroBanner id={id} isSwiper={isSwiper}>
      <StyledSwiper
        autoplay={{ delay: 5000 }}
        speed={transitionTime * 1000 * 2}
        centerInsufficientSlides
        modules={[Pagination, Autoplay]}
        pagination={{ clickable: true }}
        direction={isDesktop ? "vertical" : "horizontal"}
        slidesPerView={1}
        spaceBetween={0}
      >
        {currentItems?.blocks.map((slide: any, index: number) => {
          let currSlide = slide
          if (currSlide.properties)
            currSlide = transformProperties(currSlide.properties)
          const {
            title,
            subtitle,
            button,
            additionalButton,
            image,
            color,
            backgroundColor,
            text,
            whiteText,
            youTubeLink,
          } = currSlide
          const currColor = color || backgroundColor

          // Check if we have media items
          const mediaItem = image?.mediaItems?.[0]
          const youtubeLink = youTubeLink?.links?.[0]?.url

          const mediaUrl = mediaItem?.url
            ? getUmbracoMediaUrl(mediaItem.url)
            : null
          const isLottie = mediaUrl ? isLottieFile(mediaUrl) : false
          const isVideo = mediaUrl ? isVideoUrl(mediaUrl) : false
          return (
            <StyledSwiperSlide
              key={`HeroBanner-StyledSwiperSlide-${index}`}
              bgcolor={
                currColor?.value
                  ? currColor?.value.trim()
                  : colors?.lighterRed?.replace("#", "")
              }
            >
              <StyledLimitWidth>
                {breadcrumbs && (
                  <CustomBreadcrumbs
                    breadcrumbs={breadcrumbs}
                    textColor={whiteText?.value ? colors.white : colors.black}
                    loadSchema={index === 0}
                  />
                )}
                <StyledTitlesTextContainer whiteText={whiteText?.value}>
                  {subtitle?.value && (
                    <StyledSubTitle>{subtitle?.value}</StyledSubTitle>
                  )}
                  {(title?.value || title?.richText) && (
                    <StyledTitle
                      variant={index === 0 ? "h1" : "h2"}
                      dangerouslySetInnerHTML={{
                        __html: replaceRichTextLinks(
                          removeWrappingPTags(title?.value || title?.richText),
                        ),
                      }}
                    />
                  )}
                  {text?.value && (
                    <StyledText variant="h5" component="p">
                      {text?.value}
                    </StyledText>
                  )}
                  {(button || additionalButton) && (
                    <StyledButtonsWrapper>
                      {additionalButton && additionalButton?.links[0] && (
                        <ButtonWithArrow
                          href={getUrlWithUtm(additionalButton?.links[0]?.url)}
                          variant={
                            currColor?.value.toUpperCase() === "141B4D"
                              ? "elevated"
                              : undefined
                          }
                          colorPallette={
                            currColor?.value.toUpperCase() === "141B4D"
                              ? "turquoise"
                              : undefined
                          }
                          onClick={() =>
                            sendBlockEvent({
                              blockId: id || "1",
                              title: title?.value,
                              componentName: "באנר",
                              productName:
                                data?.productName ||
                                data?.formDefaultInsuranceType,
                              buttonName: additionalButton.links[0]?.name,
                            })
                          }
                        >
                          {additionalButton.links[0]?.name}
                        </ButtonWithArrow>
                      )}
                      {button && button?.links[0] && (
                        <ButtonWithArrow
                          colorPallette="turquoise"
                          href={getUrlWithUtm(button?.links[0]?.url)}
                          variant="elevated"
                          onClick={() =>
                            sendBlockEvent({
                              blockId: id || "1",
                              title: title?.value,
                              componentName: "באנר",
                              productName:
                                data?.productName ||
                                data?.formDefaultInsuranceType,
                              buttonName: button.links[0]?.name,
                            })
                          }
                        >
                          {button.links[0]?.name}
                        </ButtonWithArrow>
                      )}
                    </StyledButtonsWrapper>
                  )}
                </StyledTitlesTextContainer>
                {mediaUrl && isLottie ? (
                  <StyledImageContainer>
                    <LottiePlayer
                      lottieUrl={mediaUrl}
                      width={"100%"}
                      height={"auto"}
                      playOnce={false}
                      autoplay={true}
                      loop={false}
                      ariaLabel={mediaItem.altText || "hero banner animation"}
                    />
                  </StyledImageContainer>
                ) : mediaUrl && !isVideo ? (
                  <StyledImageContainer>
                    <StyledImage
                      priority={index === 0}
                      width={750}
                      height={800}
                      src={mediaUrl}
                      alt={mediaItem.altText || "hero banner image"}
                    />
                  </StyledImageContainer>
                ) : (
                  (isVideo || youtubeLink) && (
                    <StyledVideoContainer>
                      {/* Explicitly prioritize mediaUrl over youTubeUrl when both are available */}
                      <VideoContainer
                        url={mediaUrl || youtubeLink}
                        title={title?.value}
                        autoplay={true}
                      />
                    </StyledVideoContainer>
                  )
                )}
              </StyledLimitWidth>
            </StyledSwiperSlide>
          )
        })}
      </StyledSwiper>
    </StyledHeroBanner>
  )
}

export default HeroBanner
